import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Coffee, Cake, Sparkles, Home, Users, Dog, Utensils, Save, Loader2, StickyNote, ChevronDown, ChevronUp } from "lucide-react";

const HOT_DRINK_PRESETS = ["Coffee — black", "Coffee — white", "Latte", "Cappuccino", "Americano", "Espresso", "Tea — builder's", "Tea — Earl Grey", "Herbal tea", "Water"];
const VALET_CHOICES = [
  { value: "", label: "Not set" },
  { value: "always", label: "Always wants a fresh valet" },
  { value: "never", label: "Never — customer prefers no valet" },
  { value: "ask", label: "Ask each time" },
];

function daysUntilBirthday(iso) {
  if (!iso) return null;
  const m = iso.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [_, mm, dd] = m;
  const now = new Date();
  const thisYear = new Date(now.getFullYear(), Number(mm) - 1, Number(dd));
  const nextYear = new Date(now.getFullYear() + 1, Number(mm) - 1, Number(dd));
  const target = thisYear >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) ? thisYear : nextYear;
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
}

/**
 * Inline concierge-memory panel on the JobEditor. Loads any existing profile
 * for the job's customer_phone / customer_email and lets the advisor edit
 * the 8 preset fields + concierge notes without leaving the job.
 */
export default function CustomerProfilePanel({ job }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);
  const [dirty, setDirty] = useState(false);

  const phone = job?.customer_phone || "";
  const email = job?.customer_email || "";
  const canFetch = !!(phone || email);

  const load = useCallback(async () => {
    if (!canFetch) { setLoading(false); return; }
    try {
      const r = await api.get("/customers/profile", { params: { phone, email } });
      const p = r.data?.profile;
      setProfile(p);
      setForm({
        birthday: p?.birthday || "",
        hot_drink: p?.hot_drink || "",
        valet_preference: p?.valet_preference || "",
        loan_car_notes: p?.loan_car_notes || "",
        collection_address: p?.collection_address || "",
        household: p?.household || "",
        pet_at_home: p?.pet_at_home || "",
        allergies: p?.allergies || "",
        concierge_notes: p?.concierge_notes || "",
      });
      setDirty(false);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [phone, email, canFetch]);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/customers/profile", {
        phone, email, customer_name: job?.customer_name || "",
        ...form,
      });
      setProfile(r.data?.profile);
      setDirty(false);
      toast.success("Customer Magic Points saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const bdayDaysAway = useMemo(() => daysUntilBirthday(form.birthday), [form.birthday]);
  const bdayChip = bdayDaysAway !== null && bdayDaysAway <= 30
    ? (bdayDaysAway === 0 ? "🎂 birthday today!" : `🎂 birthday in ${bdayDaysAway}d`)
    : null;

  if (loading) return null;
  if (!canFetch) {
    return (
      <div className="card-default p-4 text-xs text-muted-foreground" data-testid="customer-profile-panel">
        Add a phone or email on the customer record to build up their Customer Magic Points.
      </div>
    );
  }

  return (
    <div className="card-default" data-testid="customer-profile-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-muted/30 transition-colors text-left"
        data-testid="customer-profile-toggle"
      >
        <div className="flex items-center gap-3 flex-wrap min-w-0 flex-1">
          <div className="w-10 h-10 rounded-full grid place-items-center bg-warning/15 text-warning shrink-0">
            <Heart className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="label-overline text-warning flex items-center gap-1.5">
              Customer Magic Points
              <span className="text-[9px] font-mono font-bold tracking-widest text-warning/70">CMPs</span>
            </div>
            <div className="font-display font-bold text-lg mt-0.5 truncate">
              {profile ? "What we know about this customer" : "Add a note about this customer"}
            </div>
          </div>
          {bdayChip && (
            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded bg-warning/20 text-warning border border-warning/40 shrink-0" data-testid="customer-profile-bday-chip">
              {bdayChip}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Birthday" icon={<Cake className="w-3.5 h-3.5" />}>
              <input
                type="date"
                value={form.birthday}
                onChange={(e) => update("birthday", e.target.value)}
                className="input-default text-sm"
                data-testid="customer-profile-birthday"
              />
            </Field>
            <Field label="Hot drink" icon={<Coffee className="w-3.5 h-3.5" />}>
              <input
                list="hot-drink-presets"
                value={form.hot_drink}
                onChange={(e) => update("hot_drink", e.target.value)}
                placeholder="e.g. Latte, no sugar"
                className="input-default text-sm"
                data-testid="customer-profile-hot-drink"
              />
              <datalist id="hot-drink-presets">
                {HOT_DRINK_PRESETS.map((d) => <option key={d} value={d} />)}
              </datalist>
            </Field>
            <Field label="Valet preference" icon={<Sparkles className="w-3.5 h-3.5" />}>
              <select
                value={form.valet_preference}
                onChange={(e) => update("valet_preference", e.target.value)}
                className="input-default text-sm"
                data-testid="customer-profile-valet"
              >
                {VALET_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Loan car notes" icon={<Sparkles className="w-3.5 h-3.5" />}>
              <input
                value={form.loan_car_notes}
                onChange={(e) => update("loan_car_notes", e.target.value)}
                placeholder="e.g. needs child seat, prefers automatic"
                className="input-default text-sm"
                data-testid="customer-profile-loan-notes"
              />
            </Field>
            <Field label="Collection address" icon={<Home className="w-3.5 h-3.5" />}>
              <textarea
                rows={2}
                value={form.collection_address}
                onChange={(e) => update("collection_address", e.target.value)}
                placeholder="Saved address for one-tap collection jobs"
                className="input-default text-sm resize-y"
                data-testid="customer-profile-address"
              />
            </Field>
            <Field label="Household" icon={<Users className="w-3.5 h-3.5" />}>
              <textarea
                rows={2}
                value={form.household}
                onChange={(e) => update("household", e.target.value)}
                placeholder="e.g. Fiona is his wife, drives the Yaris"
                className="input-default text-sm resize-y"
                data-testid="customer-profile-household"
              />
            </Field>
            <Field label="Pet at home" icon={<Dog className="w-3.5 h-3.5" />}>
              <input
                value={form.pet_at_home}
                onChange={(e) => update("pet_at_home", e.target.value)}
                placeholder="e.g. large friendly labrador"
                className="input-default text-sm"
                data-testid="customer-profile-pet"
              />
            </Field>
            <Field label="Allergies" icon={<Utensils className="w-3.5 h-3.5" />}>
              <input
                value={form.allergies}
                onChange={(e) => update("allergies", e.target.value)}
                placeholder="e.g. nut allergy — no biscuits with hazelnut"
                className="input-default text-sm"
                data-testid="customer-profile-allergies"
              />
            </Field>
          </div>

          <Field label="Customer notes" icon={<StickyNote className="w-3.5 h-3.5" />}>
            <textarea
              rows={3}
              value={form.concierge_notes}
              onChange={(e) => update("concierge_notes", e.target.value)}
              placeholder="Anything else worth remembering — appreciated by name, hates waiting, always in a rush…"
              className="input-default text-sm resize-y"
              data-testid="customer-profile-notes"
            />
          </Field>

          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              {profile ? `Last updated ${new Date(profile.updated_at).toLocaleDateString("en-GB")}` : "New profile"}
            </span>
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="customer-profile-save-btn"
            >
              {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : <><Save className="w-3 h-3" /> Save</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}
